import { Alert, Divider, Stack, Tooltip, Typography } from '@mui/joy';
import { LockSimple, Warning } from '@phosphor-icons/react';
import dayjs from 'dayjs';
import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser, useUserPermissions } from 'auth/hooks';
import { BillToLocationFormField } from 'contacts/elements';
import { contactGroupService } from 'contacts/services';
import { IContactGroupAddress } from 'contacts/types';
import { FormField, MultiSelectChipList } from 'core/components';
import { AccessPermissions, InputType, LabelPosition } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { useFormContext } from 'core/states';
import { GetManyResponse } from 'core/types';
import { CorporateLocationFormField, CorporateUserFormField } from 'corporate/elements';
import { CorporateLocationType } from 'corporate/enums';
import { corporateLocationService } from 'corporate/services';
import { ICorporateLocationAutocomplete, ITradeAgreementOwner } from 'corporate/types';
import { ProjectFormField } from 'projects/components';
import { projectService } from 'projects/services';
import { IProject, IProjectAutocomplete, IProjectAutocompleteRequestPayload } from 'projects/types';
import { AttributeProjectKind } from 'quotes/enums';
import { VendorQuoteHelper } from 'quotes/helpers';
import { useGetProjectCustomers } from 'quotes/hooks';
import { Color, Level, Orientation, Size, Variant } from 'theme/enums';

import {
  AttributePriorityMode,
  getAttributeProjectKind,
  mapAttributeCustomersToForm,
} from './mappers';
import { ALL_CUSTOMERS_ALL_SALES_LOCATIONS_TEST } from './schema';
import {
  getSpaPriorityTierOptions,
  getSpaStatusChoices,
  includeSelectedOption,
  prependSentinelOption,
  PROJECT_REQUEST_PAYLOAD,
  renderArNumber,
  renderLocationId,
  renderProjectOption,
  renderSalesLocationSecondary,
  renderTradeAgreementOwnerOption,
  TRADE_AGREEMENT_OWNER_REQUEST_PAYLOAD,
} from './utils';
import {
  ALL_CUSTOMERS_OPTION_ID,
  ALL_SALES_LOCATIONS_OPTION_ID,
  SpaPriorityTier,
} from '../../enums';
import { IAttributeFormLocation } from '../../types';

interface IProps {
  // Selection mode the form was hydrated with, so the priority re-sync doesn't
  // clobber a saved/overridden tier.
  hydratedPriorityMode?: AttributePriorityMode;
  // Caps the Trade Agreement Start Date picker. Absent on VQ import, where the quote does not
  // exist yet, and on a quote with no Valid Until — both leave the picker unbounded.
  validUntil?: string | null;
  // Renders the stored attributes without offering an edit. Set by the SKU export drawer when the
  // BE says the user cannot edit the Attributes.
  isReadOnly?: boolean;
}

export const AttributeFormFields = ({
  hydratedPriorityMode = null,
  validUntil = null,
  isReadOnly = false,
}: IProps) => {
  // Set only on a user pick: a hydrated project already carries its derived customers.
  const [requestedProjectId, setRequestedProjectId] = useState<number | null>(null);

  const { t } = useTranslation();
  const { user } = useAuthUser();
  const { hasPermission } = useUserPermissions();
  const { clearErrors, control, errors, setValue, watch } = useFormContext();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ user });
  const { canAllowAllCustomersSpa, canAllowAllCustomersAllLocationsSpa, canAllowProgramSpa } =
    VendorQuoteHelper.getSpaAudienceRights(user);

  // Hidden without their role resources; the summary card still shows the stored values to all.
  const canEnableSelectableSpa = hasPermission(AccessPermissions.SELECTABLE_SPA);
  const canChooseSpaStatus = hasPermission(AccessPermissions.ALLOW_LIVE_OR_PLANNING_SPA);

  // undefined (not null) so the picker stays unbounded when the quote never expires or the
  // stored value is unparseable — MUI treats an invalid maxDate as "nothing selectable".
  const tradeAgreementStartMaxDate = useMemo(() => {
    const parsed = dayjs(validUntil ?? undefined);

    return validUntil && parsed.isValid() ? parsed : undefined;
  }, [validUntil]);

  const projectValue = watch('project') as IProjectAutocomplete | null;
  const customersValue = watch('customers') as IContactGroupAddress[] | undefined;
  const allCustomersValue = watch('allCustomers') as boolean | undefined;
  const allSalesLocationsValue = watch('allSalesLocations') as boolean | undefined;
  const salesLocationsValue = watch('salesLocations') as IAttributeFormLocation[] | undefined;
  const tradeAgreementOwnerValue = watch('tradeAgreementOwner') as ITradeAgreementOwner | null;
  const projectCustomersValue = watch('projectCustomers') as IContactGroupAddress[] | undefined;

  const { customers: fetchedProjectCustomers, errorMessage: projectCustomersFetchError } =
    useGetProjectCustomers(requestedProjectId);

  const isProjectSelected = !!projectValue?.id;
  const isCustomersSelected = (customersValue?.length ?? 0) > 0 || !!allCustomersValue;
  const projectKind = getAttributeProjectKind(projectValue);
  const isProjectKind = AttributeProjectKind.PROJECT === projectKind;
  const isProgramKind = AttributeProjectKind.PROGRAM === projectKind;
  // A program is sent as All Customers, so it shares the All Sales Locations restriction.
  const isAllCustomersAudience = !!allCustomersValue || isProgramKind;

  // The All Customers option needs the existing right; next to All Sales Locations it also needs
  // the new one, and so does All Sales Locations next to the All Customers audience. A stored
  // selection beyond the rights is refused by the schema until it is narrowed.
  const isAllCustomersOptionHidden =
    !canAllowAllCustomersSpa || (!canAllowAllCustomersAllLocationsSpa && !!allSalesLocationsValue);
  const isAllSalesLocationsOptionHidden =
    !canAllowAllCustomersAllLocationsSpa && isAllCustomersAudience;
  const isBothAllNotPermitted = isAllSalesLocationsOptionHidden && !!allSalesLocationsValue;
  const isAllCustomersNotPermitted = !canAllowAllCustomersSpa && !!allCustomersValue;
  const salesLocationsErrorType = (errors?.salesLocations as { type?: string } | undefined)?.type;
  const projectCustomersErrorMessage =
    projectCustomersFetchError ??
    (errors?.projectCustomers as { message?: string } | undefined)?.message ??
    null;

  // Kept as a primitive so the re-sync effect fires only on a project<->customers
  // transition, not every keystroke.
  const priorityMode: AttributePriorityMode = isProjectSelected
    ? 'project'
    : isCustomersSelected
    ? 'customers'
    : null;

  // Seeded with the hydrated mode so the first effect run for an already-hydrated
  // selection is a no-op (preserves the saved tier).
  const prevPriorityModeRef = useRef<AttributePriorityMode>(hydratedPriorityMode);

  useEffect(() => {
    clearErrors(['project', 'customers', 'projectCustomers']);
  }, [
    projectValue?.id,
    customersValue?.length,
    allCustomersValue,
    projectCustomersValue?.length,
    clearErrors,
  ]);

  // shouldDirty so the fill survives FormProvider's keepDirtyValues reset on a ['quote']
  // invalidation, which would otherwise bring back the stored project's customers.
  useEffect(() => {
    if (!requestedProjectId || !fetchedProjectCustomers) {
      return;
    }

    if (requestedProjectId !== projectValue?.id) {
      return;
    }

    setValue('projectCustomers', mapAttributeCustomersToForm(fetchedProjectCustomers), {
      shouldDirty: true,
    });
  }, [fetchedProjectCustomers, projectValue?.id, requestedProjectId, setValue]);

  useEffect(() => {
    if (
      !isBothAllNotPermitted &&
      salesLocationsErrorType === ALL_CUSTOMERS_ALL_SALES_LOCATIONS_TEST
    ) {
      clearErrors('salesLocations');
    }
  }, [isBothAllNotPermitted, salesLocationsErrorType, clearErrors]);

  // SPA priority default: project -> Priority 1, customers -> Priority 3. Re-syncs only
  // on a real mode switch, so manual overrides survive.
  useEffect(() => {
    if (priorityMode === prevPriorityModeRef.current) {
      return;
    }

    prevPriorityModeRef.current = priorityMode;

    if (priorityMode === 'project') {
      setValue('spaPriorityTier', SpaPriorityTier.PRIORITY_1);
    } else if (priorityMode === 'customers') {
      setValue('spaPriorityTier', SpaPriorityTier.PRIORITY_3);
    }
  }, [priorityMode, setValue]);

  const firstSalesLocationOwner = salesLocationsValue?.[0]?.tradeAgreementOwner ?? null;
  const hasTradeAgreementOwner = !!tradeAgreementOwnerValue?.id;

  // Prefill the owner once per sales-location change, not whenever the field is empty — else
  // the clear (X) button is inert, since the effect refills on the very next render. A manually
  // picked owner is never overwritten: the field must be empty for the prefill to land.
  const prefilledFromOwnerIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!firstSalesLocationOwner?.id) {
      prefilledFromOwnerIdRef.current = null;

      return;
    }

    if (prefilledFromOwnerIdRef.current === firstSalesLocationOwner.id) {
      return;
    }

    prefilledFromOwnerIdRef.current = firstSalesLocationOwner.id;

    if (hasTradeAgreementOwner) {
      return;
    }

    // shouldDirty so the prefill survives FormProvider's keepDirtyValues reset, which fires
    // whenever defaultValues change identity (a ['quote'] invalidation) and would otherwise
    // restore the stale server value over it.
    setValue('tradeAgreementOwner', firstSalesLocationOwner, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [firstSalesLocationOwner, hasTradeAgreementOwner, setValue]);

  // Prepend a sentinel "All Customers" option, only on the first page and only when
  // the query is empty or matches its label.
  const getCustomerOptionsWithAllCustomers = useCallback(
    async (
      requestPayload: { page?: number; filters?: { query?: string } } & object,
      signal?: AbortSignal
    ): Promise<GetManyResponse<IContactGroupAddress>> => {
      const response = (await contactGroupService.getContactGroupAddressesAutocomplete(
        requestPayload,
        signal
      )) as GetManyResponse<IContactGroupAddress>;

      if (isAllCustomersOptionHidden) {
        return response;
      }

      return prependSentinelOption({
        response,
        isFirstPage: !requestPayload.page || requestPayload.page === 1,
        label: t('labelAllCustomers'),
        query: requestPayload.filters?.query,
        buildOption: () =>
          ({
            id: ALL_CUSTOMERS_OPTION_ID,
            name: t('labelAllCustomers'),
            line1: '',
            line2: '',
            city: '',
            state: '',
            stateName: '',
            zip: '',
            country: '',
            countryName: '',
          }) as unknown as IContactGroupAddress,
      });
    },
    [isAllCustomersOptionHidden, t]
  );

  // The "All Customers" sentinel toggles the `allCustomers` flag instead of landing in
  // the real `customers` array.
  const handleCustomerPick = useCallback(
    (
      pickerName: string,
      append: (event: SyntheticEvent, option: IContactGroupAddress) => void,
      replace: (items: IContactGroupAddress[]) => void,
      event: SyntheticEvent,
      option: IContactGroupAddress
    ) => {
      if (option?.id === ALL_CUSTOMERS_OPTION_ID) {
        // The picker can still list the sentinel from its cached options until it refetches.
        if (isAllCustomersOptionHidden) {
          setValue(pickerName, null, { shouldValidate: false });

          return;
        }

        setValue('allCustomers', true);
        // Clear via the useFieldArray API, not a raw setValue on the array name (RHF footgun).
        replace([]);
        setValue(pickerName, null, { shouldValidate: false });

        return;
      }

      setValue('allCustomers', false);
      append(event, option);
    },
    [isAllCustomersOptionHidden, setValue]
  );

  const handleClearAllCustomers = useCallback(() => {
    setValue('allCustomers', false, { shouldValidate: true });
  }, [setValue]);

  // Prepend a sentinel "All Locations" option, only on the first page and only when the
  // query is empty or matches its label. Mirrors getCustomerOptionsWithAllCustomers.
  const getSalesLocationOptionsWithAllLocations = useCallback(
    async (
      // The picker uses CorporateLocationAutocomplete, which leaves includeFilterQuery=false,
      // so the core Autocomplete sends the typed text as a top-level `q` — not under filters.query.
      requestPayload: { page?: number; filters?: { query?: string }; q?: string } & object,
      signal?: AbortSignal
    ): Promise<GetManyResponse<ICorporateLocationAutocomplete>> => {
      const response = await corporateLocationService.getLocationsAutocomplete(
        requestPayload,
        signal
      );

      if (isAllSalesLocationsOptionHidden) {
        return response;
      }

      return prependSentinelOption({
        response,
        isFirstPage: !requestPayload.page || requestPayload.page === 1,
        label: t('labelAllLocations'),
        query: requestPayload.q,
        buildOption: (): ICorporateLocationAutocomplete => ({
          id: ALL_SALES_LOCATIONS_OPTION_ID,
          name: t('labelAllLocations'),
          externalLocationId: '',
          isAlternateSales: 0,
          isAlternateShipping: 0,
          isSales: 0,
          isShipping: 0,
          inventoryQuantity: 0,
          vendorId: '',
        }),
      });
    },
    [isAllSalesLocationsOptionHidden, t]
  );

  // The "All Locations" sentinel toggles the `allSalesLocations` flag instead of landing in
  // the real `salesLocations` array. Mirrors handleCustomerPick.
  const handleSalesLocationPick = useCallback(
    (
      pickerName: string,
      append: (event: SyntheticEvent, option: IAttributeFormLocation) => void,
      replace: (items: IAttributeFormLocation[]) => void,
      event: SyntheticEvent,
      option: ICorporateLocationAutocomplete
    ) => {
      if (option?.id === ALL_SALES_LOCATIONS_OPTION_ID) {
        if (isAllSalesLocationsOptionHidden) {
          setValue(pickerName, null, { shouldValidate: false });

          return;
        }

        setValue('allSalesLocations', true);
        replace([]);
        setValue(pickerName, null, { shouldValidate: false });

        return;
      }

      setValue('allSalesLocations', false);
      // The picked record's name is optional; the chip renders it unconditionally.
      append(event, { ...option, name: option.name ?? '' });
    },
    [isAllSalesLocationsOptionHidden, setValue]
  );

  const handleClearAllSalesLocations = useCallback(() => {
    setValue('allSalesLocations', false, { shouldValidate: true });
  }, [setValue]);

  // Display only: the BE sends a program as All Customers, so `allCustomers` stays unset.
  const programAllCustomersChip = useMemo(
    () => ({ label: t('labelAllCustomers'), onDelete: () => undefined }),
    [t]
  );

  // `projectValue` is still the previous pick here (the field sets the value, then calls this in
  // the same event). A same-id re-pick keeps the list: the fetch is cached and the ids are equal,
  // so the fill effect would never run again after a reset.
  const previousProjectId = projectValue?.id;
  const handleProjectChange = useCallback(
    (project: IProjectAutocomplete | null) => {
      if (project?.id && project.id === previousProjectId) {
        return;
      }

      setValue('projectCustomers', [], { shouldDirty: true });
      setRequestedProjectId(
        AttributeProjectKind.PROJECT === getAttributeProjectKind(project) ? project.id : null
      );
    },
    [previousProjectId, setValue]
  );

  // The BE lists programs to everyone and refuses the save; without the right they are not offered.
  // Filtering client-side can leave a page shorter than the page size.
  const getProjectOptions = useCallback(
    async (
      requestPayload: IProjectAutocompleteRequestPayload,
      signal?: AbortSignal
    ): Promise<GetManyResponse<IProject>> => {
      const response = await projectService.getManyAutocomplete(requestPayload, signal);
      const permittedResponse = canAllowProgramSpa
        ? response
        : {
            ...response,
            records: (response?.records ?? []).filter(
              (project) => AttributeProjectKind.PROGRAM !== getAttributeProjectKind(project)
            ),
          };

      return includeSelectedOption<IProject>({
        response: permittedResponse,
        isFirstPage: !requestPayload.page || requestPayload.page === 1,
        selected: projectValue as IProject | null,
      });
    },
    [canAllowProgramSpa, projectValue]
  );

  const spaPriorityTierOptions = getSpaPriorityTierOptions();

  const projectField = (
    <ProjectFormField
      defaultDtdProject={false}
      required={false}
      // Lands focus on the first usable input, as the removed TAC field used to.
      autoFocus
      disabled={isReadOnly || isCustomersSelected}
      disableClearable={false}
      placeholder={t('placeholderSelectProject')}
      requestPayload={PROJECT_REQUEST_PAYLOAD}
      getOptions={getProjectOptions}
      onChange={handleProjectChange}
      renderOptions={renderProjectOption}
    />
  );

  const projectCustomersField = (
    <Stack sx={{ gap: 0.5 }}>
      <MultiSelectChipList
        name="projectCustomers"
        label={t('labelCustomers')}
        renderItemSecondary={renderArNumber}
        renderPicker={() => null}
        disabled
      />

      <Typography
        level={Level.BODY_SM}
        color={projectCustomersErrorMessage ? Color.DANGER : Color.NEUTRAL}
      >
        {projectCustomersErrorMessage ?? t('messageSpaCustomersFromProject')}
      </Typography>
    </Stack>
  );

  const programCustomersField = (
    <Stack sx={{ gap: 0.5 }}>
      <MultiSelectChipList
        name="customers"
        label={t('labelCustomers')}
        pinnedChip={programAllCustomersChip}
        renderPicker={() => null}
        disabled
      />

      <Typography level={Level.BODY_SM} color={Color.NEUTRAL}>
        {t('messageSpaProgramSentToAllCustomers')}
      </Typography>
    </Stack>
  );

  return (
    <Stack sx={{ gap: 3 }}>
      {isReadOnly && (
        <Alert
          startDecorator={<LockSimple />}
          variant={Variant.SOFT}
          color={Color.NEUTRAL}
          size={Size.SM}
        >
          <Typography level={Level.BODY_SM}>{t('tooltipAttributesEditBlocked')}</Typography>
        </Alert>
      )}

      {isAllCustomersNotPermitted && (
        <Alert
          startDecorator={<Warning />}
          variant={Variant.SOFT}
          color={Color.DANGER}
          size={Size.SM}
        >
          <Typography level={Level.BODY_SM} color={Color.DANGER}>
            {t('messageAllCustomersSpaNotPermitted')}
          </Typography>
        </Alert>
      )}

      {isBothAllNotPermitted && (
        <Alert
          startDecorator={<Warning />}
          variant={Variant.SOFT}
          color={Color.DANGER}
          size={Size.SM}
        >
          <Typography level={Level.BODY_SM} color={Color.DANGER}>
            {t('messageAllCustomersAllSalesLocationsNotAllowed')}
          </Typography>
        </Alert>
      )}

      {/* Project, "or" and Customers share a tight gap so the divider reads as a choice
          between those two fields rather than a separator between form sections. */}
      <Stack sx={{ gap: 0.5 }}>
        {/* The "disabled by customers" wording is wrong under the read-only lock, which has its
            own banner. */}
        {isCustomersSelected && !isReadOnly ? (
          <Tooltip title={t('messageExportSkuPartsProjectAlreadyDisabledByCustomers')}>
            <span>{projectField}</span>
          </Tooltip>
        ) : (
          projectField
        )}

        {/* A project's customers are derived and a program goes to All Customers: neither is a
            choice, so no "or" and a locked list. */}
        {isProjectKind && projectCustomersField}

        {isProgramKind && programCustomersField}

        {/* Surfaces the project XOR customers rule before validation fires. */}
        {!projectKind && <Divider>{t('labelOr')}</Divider>}

        {!projectKind && (
          <MultiSelectChipList
            name="customers"
            label={t('labelCustomers')}
            placeholder={t('placeholderSearchCustomers')}
            renderItemSecondary={renderArNumber}
            pinnedChip={
              allCustomersValue
                ? { label: t('labelAllCustomers'), onDelete: handleClearAllCustomers }
                : undefined
            }
            disabled={isReadOnly}
            renderPicker={({ name, onChange, placeholder, disabled, replace }) => {
              const append = onChange as unknown as (
                event: SyntheticEvent,
                option: IContactGroupAddress
              ) => void;

              return (
                <BillToLocationFormField
                  name={name}
                  label=""
                  placeholder={placeholder}
                  getOptions={getCustomerOptionsWithAllCustomers}
                  onChange={(event, option) =>
                    handleCustomerPick(name, append, replace, event, option as IContactGroupAddress)
                  }
                  hasCreateOption={false}
                  required={false}
                  disabled={disabled}
                  disableClearable={false}
                  isAlternateVersionEnabled={isAlternateVersionEnabled}
                  hideCustomerNumber
                  clearInputOnSelect
                />
              );
            }}
          />
        )}
      </Stack>

      <FormField
        name="spaPriorityTier"
        type={InputType.SELECT}
        inputProps={{
          control,
          error: errors?.spaPriorityTier,
          label: t('labelSpaPriority'),
          placeholder: t('placeholderSelectSpaPriority'),
          options: spaPriorityTierOptions,
          required: true,
          disabled: isReadOnly,
        }}
      />

      {canChooseSpaStatus && (
        <FormField
          name="spaStatus"
          type={InputType.RADIO_GROUP}
          inputProps={{
            control,
            error: errors?.spaStatus,
            label: t('labelSpaStatus'),
            orientation: Orientation.HORIZONTAL,
            choices: getSpaStatusChoices(),
            required: true,
            disabled: isReadOnly,
          }}
        />
      )}

      <MultiSelectChipList<IAttributeFormLocation>
        name="salesLocations"
        label={t('labelSalesLocations')}
        placeholder={t('placeholderSearchSalesLocations')}
        renderItemSecondary={renderSalesLocationSecondary}
        disabled={isReadOnly}
        pinnedChip={
          allSalesLocationsValue
            ? { label: t('labelAllLocations'), onDelete: handleClearAllSalesLocations }
            : undefined
        }
        renderPicker={({ name, onChange, placeholder, disabled, replace }) => {
          const append = onChange;

          return (
            <CorporateLocationFormField
              name={name}
              label=""
              placeholder={placeholder}
              disabled={disabled}
              getOptions={getSalesLocationOptionsWithAllLocations}
              onChange={(event, option) =>
                handleSalesLocationPick(
                  name,
                  append,
                  replace,
                  event,
                  option as ICorporateLocationAutocomplete
                )
              }
              required={false}
              disableClearable={false}
              isAlternateVersionEnabled={isAlternateVersionEnabled}
              type={CorporateLocationType.SALES}
              clearInputOnSelect
            />
          );
        }}
      />

      <MultiSelectChipList<IAttributeFormLocation>
        name="shippingLocations"
        label={t('labelShippingLocations')}
        placeholder={t('placeholderSearchShippingLocations')}
        renderItemSecondary={renderLocationId}
        disabled={isReadOnly}
        renderPicker={({ name, onChange, placeholder, disabled }) => (
          <CorporateLocationFormField
            name={name}
            label=""
            placeholder={placeholder}
            disabled={disabled}
            onChange={(event, option) => {
              const location = option as ICorporateLocationAutocomplete;

              onChange(event, { ...location, name: location.name ?? '' });
            }}
            required={false}
            disableClearable={false}
            isAlternateVersionEnabled={isAlternateVersionEnabled}
            type={CorporateLocationType.SHIPPING}
            clearInputOnSelect
          />
        )}
      />

      <CorporateUserFormField
        name="tradeAgreementOwner"
        label={t('labelTradeAgreementOwner')}
        placeholder={t('placeholderSelectTradeAgreementOwner')}
        requestPayload={TRADE_AGREEMENT_OWNER_REQUEST_PAYLOAD}
        renderOptions={renderTradeAgreementOwnerOption}
        required
        disableClearable={false}
        disabled={isReadOnly}
      />

      <FormField
        name="tradeAgreementStartDate"
        type={InputType.DATE_INPUT}
        inputProps={{
          label: t('labelTradeAgreementStartDate'),
          tooltip: t('tooltipTradeAgreementStartDate'),
          control,
          error: errors?.tradeAgreementStartDate,
          // Stops the invalid choice at the picker. The schema keeps the same rule, because
          // Valid Until can be changed on the quote after a start date was picked.
          maxDate: tradeAgreementStartMaxDate,
          required: true,
          disabled: isReadOnly,
        }}
      />

      {canEnableSelectableSpa && (
        <FormField
          name="selectableSpaEnabled"
          type={InputType.SWITCH}
          inputProps={{
            control,
            label: t('labelEnabledForSelectedSpa'),
            labelPosition: LabelPosition.END,
            disabled: isReadOnly,
          }}
        />
      )}
    </Stack>
  );
};
